import { useEffect, useState, type RefObject } from "react";

/** The desktop edge, repeated from `tokens/grid.css` — see below. */
const DESKTOP_MIN_WIDTH = "(min-width: 1280px)";

/** What one measurement of the chip row says about the clamp. */
export interface ChipOverflow {
  /** The chips occupy more rows than the limit, so Show More is needed. */
  overflowing: boolean;
  /**
   * Index of the first chip the clamp cuts off, or null while they all fit.
   * ⚠ Null is also the UNMEASURED state, which is the safe one: nothing is
   * marked clipped until a measurement says which chips are.
   */
  clippedFrom: number | null;
}

const FITS: ChipOverflow = {
  overflowing: false,
  clippedFrom: null
};

/**
 * SR7 — whether the chips occupy more rows than the limit, so the Show More
 * control renders, and so it goes away again once they fit; and which chips
 * the clamp cuts off, which AX-wise must not keep a tab stop (SC 2.4.11).
 *
 * ⚠ ROWS ARE COUNTED FROM `offsetTop`, not from `scrollHeight` against a
 * clamped `clientHeight`. The clamp is off while the chips are expanded, so a
 * height comparison reads "they fit" the moment the reader opens them and the
 * control removes itself mid-use. The number of distinct row offsets is the
 * same in both states.
 *
 * ⚠ The limit differs by breakpoint (SR7: 3 on desktop, 5 on tablet and
 * mobile — the same limit for both, so only the desktop edge needs asking)
 * and both values are props, which a media query cannot read — so the query
 * is asked here instead, against the same literal `tokens/grid.css`
 * publishes. A custom property is invalid inside `@media`.
 *
 * Neither `ResizeObserver` nor `matchMedia` exists in jsdom. Both are optional
 * here: without them the hook reports "fits" rather than throwing.
 */
export function useChipOverflow(
  listRef: RefObject<HTMLElement | null>,
  rowLimitDesktop: number,
  rowLimitMobile: number,
  chipCount: number
): ChipOverflow {
  const [overflow, setOverflow] = useState<ChipOverflow>(FITS);

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return undefined;
    }

    const measure = () => {
      const desktop = window.matchMedia?.(DESKTOP_MIN_WIDTH).matches ?? false;
      const limit = desktop ? rowLimitDesktop : rowLimitMobile;
      const tops = Array.from(list.children).map(
        child => (child as HTMLElement).offsetTop
      );
      const rows = Array.from(new Set(tops)).sort((a, b) => a - b);
      /* The offset of the first row PAST the limit, and undefined when there
         is none — which is the same question as `rows.length > limit`, asked
         once so the two answers cannot disagree. */
      const clipped = rows[limit];
      const next = clipped === undefined ?
        FITS :
        {
          overflowing: true,
          /* Chips lay out in DOM order, so the first one on a clipped row is
             the first clipped one. */
          clippedFrom: tops.findIndex(top => top >= clipped)
        };
      /* The observer fires on every resize, most of which change neither
         answer; a fresh object each time would re-render the whole field. */
      setOverflow(current => (current.overflowing === next.overflowing &&
        current.clippedFrom === next.clippedFrom ?
        current :
        next));
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    /* The chips rewrap when the container changes width, which no event
       reports. The chip count is already a dependency, so the observer only
       has to cover the resize. */
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, rowLimitDesktop, rowLimitMobile, chipCount]);

  return overflow;
}
